import React, { Suspense, useMemo } from 'react';

import {
	SidebarScrollContainer,
	SidebarContainerBorder,
	SidebarContainerPane,
	SidebarBottomFixedContainer,
} from '@atlassian/search-dialog/search-dialog.styled';

import {
	SITE_FILTER_TYPE_NAME,
	FilterUIVariant,
	type FilterDefinition,
	type GenericFilterOption,
} from '../../../../../filters/filter-types';
import {
	useCurrentProductGenericSelectedFilterCount,
	useGetCurrentProductFilters,
} from '../../../../../filters/filters-helper-functions';
import { useProductContext } from '../../../../product-router';
import { ProductStates } from '../../../../../product-state-machine/product-state-machine-types';
import { CheckboxListFilter } from './checkbox-list-filter';
import ExtensibleFeedbackCollector from '../../../../../feedback-collector/feedback-collector-async';
import { useQuery } from '../../../../../query-context/query-context';
import MoreFiltersLink from '../../../../../../common/filters/more-filters/more-filters';
import { useSessionUserInput } from '../../../../../user-input-provider/user-input-context';
import { MoreFiltersContainer } from '../../../../../../common/filters/more-filters/more-filters.styled';

const StaticSelectFilter = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/async-chunk/static-select-filter" */ './static-select-filter'
	).then(({ StaticSelectFilter }) => ({
		default: StaticSelectFilter,
	})),
);

const MultiSelectFilter = React.lazy(() =>
	import(
		/* webpackChunkName: "@atlaskit-internal_@atlassian/product-search-dialog/async-chunk/multi-select-filter" */ './multi-select-filter'
	).then(({ MultiSelectFilter }) => ({
		default: MultiSelectFilter,
	})),
);

export const MAX_SELECTED_SITES = 5;
interface FilterSectionProps {
	productState: ProductStates;
	isPreQueryFiltersEnabled?: boolean;
}

const Filters = ({
	query,
	filters,
	href,
	showViewAllFiltersLink,
}: {
	query: string;
	filters: FilterDefinition<GenericFilterOption>[];
	href: string;
	showViewAllFiltersLink: boolean;
}) => {
	const { resetSearchSession } = useSessionUserInput();
	const selectedFiltersCount = useCurrentProductGenericSelectedFilterCount();

	const siteFilters = filters.find((filter) => filter['@type'] === SITE_FILTER_TYPE_NAME);

	if (!!siteFilters) {
		siteFilters.filterConfig = {
			...siteFilters.filterConfig,
			hideIcon: true,
			usePreloadedOptions: true,
		};
	}

	const nonSiteFilters = filters.filter((filter) => filter['@type'] !== SITE_FILTER_TYPE_NAME);

	return useMemo(
		() => (
			<>
				<SidebarContainerBorder />
				<SidebarContainerPane>
					<SidebarScrollContainer>
						{!!siteFilters && (
							<CheckboxListFilter
								filterType={siteFilters['@type']}
								{...siteFilters}
								maxSelectable={MAX_SELECTED_SITES}
							/>
						)}
						{nonSiteFilters.map((nonSiteFilter) => {
							switch (nonSiteFilter.uiVariant) {
								case FilterUIVariant.MULTI_SELECT:
									return (
										<Suspense fallback={null}>
											<MultiSelectFilter filterType={nonSiteFilter['@type']} {...nonSiteFilter} />
										</Suspense>
									);
								case FilterUIVariant.STATIC_SELECT:
									return (
										<Suspense fallback={null}>
											<StaticSelectFilter
												filterType={nonSiteFilter['@type']}
												filterStore={nonSiteFilter.store}
												sectionTitle={nonSiteFilter.sectionLabel}
											/>
										</Suspense>
									);
								default:
									return (
										<CheckboxListFilter filterType={nonSiteFilter['@type']} {...nonSiteFilter} />
									);
							}
						})}
						{!siteFilters && showViewAllFiltersLink && (
							<MoreFiltersContainer>
								<MoreFiltersLink
									isLoading={false}
									href={href}
									query={query}
									selectedFiltersCount={selectedFiltersCount}
									onClick={resetSearchSession}
								/>
							</MoreFiltersContainer>
						)}
					</SidebarScrollContainer>
					<SidebarBottomFixedContainer>
						<ExtensibleFeedbackCollector />
					</SidebarBottomFixedContainer>
				</SidebarContainerPane>
			</>
		),
		[
			href,
			nonSiteFilters,
			query,
			resetSearchSession,
			selectedFiltersCount,
			showViewAllFiltersLink,
			siteFilters,
		],
	);
};

export const FilterSection: React.FC<FilterSectionProps> = ({
	productState,
	isPreQueryFiltersEnabled = false,
}) => {
	const { query } = useQuery();
	const { getActiveProduct } = useProductContext();
	const activeProduct = getActiveProduct();
	const productId = activeProduct?.id;
	const filters = useGetCurrentProductFilters();

	if (!productId || !filters.length) {
		return null;
	}

	const href = activeProduct?.generateAdvancedSearchUrl?.(query) || '';
	const showViewAllFiltersLink = !!activeProduct?.showViewAllFiltersLink;

	switch (productState) {
		case ProductStates.PostQueryTyping:
		case ProductStates.PostQueryLoading:
		case ProductStates.PostQuerySuccess:
		case ProductStates.PostQueryNoResult:
		case ProductStates.PostQueryError:
			return (
				<Filters
					query={query}
					filters={filters}
					href={href}
					showViewAllFiltersLink={showViewAllFiltersLink}
				/>
			);
		case ProductStates.Invalid:
			return null;
		default:
			if (isPreQueryFiltersEnabled) {
				return (
					<Filters
						query={query}
						filters={filters}
						href={href}
						showViewAllFiltersLink={showViewAllFiltersLink}
					/>
				);
			}
			return null;
	}
};
